import { useParams } from "react-router-dom";
import { Download, Loader2, ShieldX } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { usePublicProfileLink } from "@/hooks/use-profile-links";
import type { ProfileType } from "@/types";

const TYPE_LABELS: Record<ProfileType, { title: string; hint: string }> = {
  ikev2: { title: "IKEv2", hint: "Recommended. Built into iOS, fastest and most reliable." },
  cisco: { title: "Cisco IPsec", hint: "Legacy mode, works well on mobile networks." },
  l2tp: { title: "L2TP/IPsec", hint: "Fallback for restrictive networks." },
};

export function ProfileDownloadPage() {
  const { token } = useParams<{ token: string }>();
  const { data, isLoading, error } = usePublicProfileLink(token);

  if (isLoading) {
    return (
      <div className="flex min-h-svh items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="flex min-h-svh items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="items-center text-center">
            <ShieldX className="h-10 w-10 text-destructive" />
            <CardTitle>Link unavailable</CardTitle>
            <CardDescription>
              This profile link is invalid, revoked, expired, or has reached its download limit.
              Ask your administrator for a new one.
            </CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }

  return (
    <div className="mx-auto flex min-h-svh max-w-md flex-col justify-center p-4">
      <Card>
        <CardHeader className="space-y-1">
          <CardTitle className="text-xl">VPN profiles for {data.userName}</CardTitle>
          <CardDescription>
            Open this page on your iPhone and download a profile below.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {data.types.map((type) => (
            <div key={type} className="space-y-1">
              <Button asChild className="h-11 w-full">
                <a href={`/api/profile-links/${encodeURIComponent(token!)}/download/${type}`}>
                  <Download className="mr-2 h-4 w-4" />
                  {TYPE_LABELS[type].title} profile
                </a>
              </Button>
              <p className="text-xs text-muted-foreground">{TYPE_LABELS[type].hint}</p>
            </div>
          ))}

          <div className="rounded-md bg-muted p-3 text-xs leading-5 text-muted-foreground">
            <p className="font-medium text-foreground">How to install on iPhone:</p>
            <ol className="ml-4 list-decimal">
              <li>Tap a download button above and allow the download.</li>
              <li>Open <b>Settings</b> → <b>Profile Downloaded</b> (near the top).</li>
              <li>Tap <b>Install</b> and confirm with your passcode.</li>
              <li>Connect via <b>Settings → VPN</b>.</li>
            </ol>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
